/**
 * STTSettingsPanel — Speech-to-Text Configuration
 *
 * Settings: primary STT provider, fallback provider, streaming mode,
 * pause detection, language.
 */
import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Save as SaveIcon,
  CheckCircle as ConnectedIcon,
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../../hooks/reduxHooks';
import { saveSTTConfig, updateSTTLocal } from '../../../store/aiServicesSlice';
import { typography } from '~/theme';

interface Props {
  onSnack: (message: string, severity: 'success' | 'error' | 'info') => void;
}

const STT_PROVIDERS = [
  { id: 'assemblyai', label: 'AssemblyAI Streaming Pro v3', desc: 'Universal streaming with word-level timestamps' },
  { id: 'deepgram', label: 'Deepgram Nova-2', desc: 'Low-latency streaming with speaker diarization' },
  { id: 'openai_realtime', label: 'OpenAI Realtime', desc: 'OpenAI real-time speech API' },
];

const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Spanish' },
  { code: 'fr', label: 'French' },
  { code: 'de', label: 'German' },
  { code: 'pt', label: 'Portuguese' },
];

const STTSettingsPanel: React.FC<Props> = ({ onSnack }) => {
  const dispatch = useAppDispatch();
  const { stt, saving, loading } = useAppSelector((s) => s.aiServices);
  const [dirty, setDirty] = useState(false);

  const handleChange = (field: string, value: string | number) => {
    dispatch(updateSTTLocal({ [field]: value }));
    setDirty(true);
  };

  const handleSave = async () => {
    try {
      await dispatch(saveSTTConfig(stt)).unwrap();
      setDirty(false);
      onSnack('Speech-to-Text settings saved', 'success');
    } catch (err) {
      onSnack(String(err), 'error');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
      <Stack spacing={3}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6">Speech-to-Text Configuration</Typography>
            <Typography variant="body2" color="text.secondary">
              Configure real-time speech recognition providers and streaming parameters
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving || !dirty}
          >
            Save Changes
          </Button>
        </Box>

        <Divider />

        {/* Provider Selection */}
        <Card variant="outlined">
          <CardHeader title="Provider Selection" titleTypographyProps={{ variant: 'subtitle1', fontWeight: typography.label.fontWeight }} />
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Primary STT Provider</InputLabel>
                  <Select
                    value={stt.primary_provider}
                    label="Primary STT Provider"
                    onChange={(e) => handleChange('primary_provider', e.target.value)}
                  >
                    {STT_PROVIDERS.map((p) => (
                      <MenuItem key={p.id} value={p.id}>
                        <Stack>
                          <Typography variant="body2" fontWeight={typography.button.fontWeight}>{p.label}</Typography>
                          <Typography variant="caption" color="text.secondary">{p.desc}</Typography>
                        </Stack>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Fallback STT Provider</InputLabel>
                  <Select
                    value={stt.fallback_provider}
                    label="Fallback STT Provider"
                    onChange={(e) => handleChange('fallback_provider', e.target.value)}
                  >
                    {STT_PROVIDERS.filter((p) => p.id !== stt.primary_provider).map((p) => (
                      <MenuItem key={p.id} value={p.id}>
                        <Typography variant="body2">{p.label}</Typography>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Streaming Configuration */}
        <Card variant="outlined">
          <CardHeader title="Streaming Configuration" titleTypographyProps={{ variant: 'subtitle1', fontWeight: typography.label.fontWeight }} />
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Streaming Mode</InputLabel>
                  <Select
                    value={stt.streaming_mode}
                    label="Streaming Mode"
                    onChange={(e) => handleChange('streaming_mode', e.target.value)}
                  >
                    <MenuItem value="realtime">Real-time (WebSocket)</MenuItem>
                    <MenuItem value="batch">Batch (REST)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Pause Detection (ms)"
                  value={stt.pause_detection_ms}
                  onChange={(e) => handleChange('pause_detection_ms', parseInt(e.target.value) || 700)}
                  helperText="Silence duration before end-of-utterance detection"
                  inputProps={{ min: 200, max: 5000, step: 100 }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Language</InputLabel>
                  <Select
                    value={stt.language}
                    label="Language"
                    onChange={(e) => handleChange('language', e.target.value)}
                  >
                    {LANGUAGES.map((l) => (
                      <MenuItem key={l.code} value={l.code}>{l.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Provider Info Cards */}
        <Typography variant="subtitle1" fontWeight={typography.label.fontWeight}>Supported Providers</Typography>
        <Grid container spacing={2}>
          {STT_PROVIDERS.map((provider) => {
            const isActive = provider.id === stt.primary_provider;
            const isFallback = provider.id === stt.fallback_provider;
            return (
              <Grid item xs={12} md={4} key={provider.id}>
                <Card
                  variant="outlined"
                  sx={{
                    borderColor: isActive ? 'primary.main' : isFallback ? 'warning.main' : 'divider',
                    borderWidth: isActive || isFallback ? 2 : 1,
                  }}
                >
                  <CardContent>
                    <Stack spacing={1}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="subtitle2">{provider.label}</Typography>
                        {isActive && <Chip label="Primary" size="small" color="primary" />}
                        {isFallback && <Chip label="Fallback" size="small" color="warning" />}
                      </Box>
                      <Typography variant="caption" color="text.secondary">{provider.desc}</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <ConnectedIcon sx={{ fontSize: 14, color: 'success.main' }} />
                        <Typography variant="caption" color="success.main">Available</Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        {dirty && (
          <Chip label="Unsaved changes" color="warning" size="small" sx={{ alignSelf: 'flex-start' }} />
        )}
      </Stack>
    </Box>
  );
};

export default STTSettingsPanel;
